import React from "react";

import { CheckoutGroupSchedulePopupView } from "./CheckoutGroupSchedulePopup.view";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from "react-google-recaptcha-v3";
import { useStore } from "~/hooks/useStore";
import { login } from "~/lib/login";
import { createCheckoutSession } from "~/lib/createCheckoutSession";
import { createFreePurchase } from "~/lib/createFreePurchase";
import { validateDiscount } from "~/lib/validateDiscount";
import { AxiosError } from "axios";

function CheckoutGroupSchedulePopupInner({
  course,
  onClose,
  ticketTypes,
  unavailableTimeBlocks
}: Props) {
  const [{ user }] = useStore("authentication");

  async function onCheckoutLogin() {
    return await login();
  }

  const { executeRecaptcha } = useGoogleReCaptcha();

  async function onGetPaymentIntent(data: any) {
    try {
      if (!executeRecaptcha) throw "Execute recaptcha not yet available";

      const recaptchaToken = await executeRecaptcha(
        "MarketplaceFlexitimeCHECKOUT"
      );
      let checkoutData = { ...data, recaptchaToken };
      return await createCheckoutSession(checkoutData);
    } catch (e) {
      throw (e as AxiosError).response?.data?.message ??
        (e as AxiosError).message ??
        "Something went wrong";
    }
  }

  async function onConfirmFreeBooking(data: any) {
    try {
      if (!executeRecaptcha) throw "Execute recaptcha not yet available";

      const recaptchaToken = await executeRecaptcha(
        "MarketplaceFlexitimeCHECKOUTFree"
      );
      let checkoutData = { ...data, recaptchaToken };
      await createFreePurchase(checkoutData, (purchase: any, price = 0) => {
        let dataTrack = {
          event: "Order Completed",
          event_category: "purchase",
          event_label: "purchase",
          value: price / 100,
          order_id: purchase._id,
          email: purchase.bookingData.user.renderEmail,
          total: price / 100,
          currency: "GBP",
          products: [
            {
              course_id: purchase.bookingData.courseId
            }
          ]
        };
        if ((window as any).dataLayer) {
          (window as any).dataLayer.push(dataTrack);
        }
      });
    } catch (e) {
      console.error(
        (e as AxiosError).response?.data?.message ??
          (e as AxiosError).message ??
          "Something went wrong"
      );
    }
  }

  async function onValidateDiscount(code: string, email: string) {
    return await validateDiscount(code, email, course._id, course.schoolId);
  }

  return (
    <CheckoutGroupSchedulePopupView
      course={course}
      onCheckoutLogin={onCheckoutLogin}
      onClose={onClose}
      onConfirmFreeBooking={onConfirmFreeBooking}
      onGetPaymentIntent={onGetPaymentIntent}
      onValidateDiscount={onValidateDiscount}
      ticketTypes={ticketTypes}
      user={user}
      unavailableTimeBlocks={unavailableTimeBlocks}
    />
  );
}

export function CheckoutGroupSchedulePopup(props: Props) {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY!}
    >
      <CheckoutGroupSchedulePopupInner {...props} />
    </GoogleReCaptchaProvider>
  );
}

interface Props {
  course: any;
  onClose: () => void;
  ticketTypes: any[];
  unavailableTimeBlocks: any[];
}
